import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MapPin, MapPinOff, AlertCircle } from 'lucide-react';
import {
  useTripPhotos, useUploadPhoto, photoUrl, type PhotoKind, type TripPhoto,
} from '@/hooks/useTripPhotos';
import {
  getCurrentLocation, watermarkAndCompress, type GeoCoords,
} from '@/lib/photo-capture';
import { extractErrorMessage } from '@/lib/api';
import { useCanUploadFromGallery } from '@/lib/permissions';
import { PasteableUpload } from '@/components/common/PasteableUpload';

// PR-UPLOAD-FIX MAY 11 — was a bespoke slot grid with:
//   1. <img src={photoUrl(...)}> direct thumbnails → 401 because the
//      /photos/:id route requires Authorization header
//   2. auto-upload on file-select with no chance to pull the wrong file
//      before it left the device (Ayan's "I can't remove a wrongly chosen
//      file" complaint)
//   3. no paste-to-upload
//
// Now routes everything through PasteableUpload, which fetches view URLs
// with the JWT (AuthedImage), stages files locally before upload (so the
// user can Remove a wrongly-picked file), and accepts Cmd-V paste / drag
// & drop on top of click-to-select.
//
// Watermark + GPS + compression still happen here before the bytes leave
// the device — PasteableUpload just hands us the staged File via its
// onUpload callback.

const KINDS: PhotoKind[] = ['delivery_slip', 'fuel_receipt', 'damage'];

interface PhotoUploadFieldProps {
  tripId: string;
  tripCode: string;
  /** When true, restrict to camera-only (no gallery). PR 9b will gate this by role. */
  cameraOnly?: boolean;
  /** Callback to open lightbox externally (optional) */
  onOpenLightbox?: (photoId: string, photoIds: string[]) => void;
}

export function PhotoUploadField({
  tripId, tripCode, cameraOnly = false, onOpenLightbox: _onOpenLightbox,
}: PhotoUploadFieldProps) {
  const { t } = useTranslation();
  const __canGallery = useCanUploadFromGallery();
  const __cameraOnly = cameraOnly || !__canGallery;
  const { data: photos = [] } = useTripPhotos(tripId);
  const uploadMutation = useUploadPhoto();

  const [error, setError] = useState<string | null>(null);
  const [gpsState, setGpsState] = useState<'unknown' | 'granted' | 'denied' | 'unavailable'>('unknown');
  const [replacing, setReplacing] = useState<Partial<Record<PhotoKind, boolean>>>({});

  const photoByKind = (kind: PhotoKind): TripPhoto | null => photos.find((p) => p.kind === kind) || null;

  async function handleUpload(kind: PhotoKind, file: File) {
    setError(null);
    try {
      // 1. GPS (best-effort)
      let coords: GeoCoords | null = null;
      try {
        coords = await getCurrentLocation();
        setGpsState(coords ? 'granted' : 'denied');
      } catch { setGpsState('unavailable'); }

      // 2. Watermark + compress on-device
      const { blob, takenAt } = await watermarkAndCompress(file, tripCode, coords);

      // 3. Upload
      await uploadMutation.mutateAsync({
        tripId,
        kind,
        file: blob,
        takenAt: takenAt.toISOString(),
        lat: coords?.lat,
        lng: coords?.lng,
      });
      setReplacing((s) => ({ ...s, [kind]: false }));
    } catch (err) {
      const msg = extractErrorMessage(err);
      setError(msg);
      throw err; // surface inside PasteableUpload too
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-sm font-medium text-foreground">{t('photos.title')}</div>
        {gpsState === 'denied' && (
          <span className="inline-flex items-center gap-1 text-xs text-amber-600">
            <MapPinOff className="h-3.5 w-3.5" /> {t('photos.gpsDenied')}
          </span>
        )}
        {gpsState === 'granted' && (
          <span className="inline-flex items-center gap-1 text-xs text-emerald-600">
            <MapPin className="h-3.5 w-3.5" /> {t('photos.gpsActive')}
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {KINDS.map((kind) => {
          const existing = photoByKind(kind);
          const isReplacing = !!replacing[kind];
          const filledUrl = existing && !isReplacing
            ? photoUrl(tripId, existing.id)
            : null;
          return (
            <PasteableUpload
              key={kind}
              label={t(`photos.kind.${kind}`)}
              helperText={__cameraOnly
                ? t('photos.takePhoto')
                : 'Click, drop, or paste (Cmd+V)'}
              accept="image/*"
              maxSizeMB={25}
              currentFileUrl={filledUrl}
              onReplace={existing ? () => setReplacing((s) => ({ ...s, [kind]: true })) : undefined}
              onUpload={(f) => handleUpload(kind, f)}
            />
          );
        })}
      </div>

      {error && (
        <div className="flex items-start gap-2 p-3 rounded bg-destructive/10 border border-destructive/30 text-destructive text-sm">
          <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}
